import React, { useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { ukDay } from "@/lib/time";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { ADMIN_COMMISSION_TYPES, COMMISSION_IS_PCT } from "@/pages/hub/constants";

const KIND = { call_centre: "Call centre", media: "Media & advertising" };

/** Approve (set the terms) or decline a partner who signed themselves up from /partner-with-us. */
export function ApprovePartnerDialog({ party, onOpenChange, onDone }) {
  const [f, setF] = useState({ commission_type: "commission_share", commission_pct: "50", commission_flat_gbp: "250",
                               payout_frequency: "monthly", override_pct: "", note: "" });
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [declining, setDeclining] = useState(false);
  const sg = party?.signup || {};
  const set = (k) => (e) => setF({ ...f, [k]: e?.target ? e.target.value : e });
  const isPct = COMMISSION_IS_PCT(f.commission_type);
  const model = ADMIN_COMMISSION_TYPES.find((c) => c.key === f.commission_type);

  const approve = async () => {
    setBusy(true);
    try {
      const body = {
        commission_type: f.commission_type,
        commission_pct: isPct ? Number(f.commission_pct || 0) : 0,
        commission_flat_gbp: isPct ? 0 : Number(f.commission_flat_gbp || 0),
        payout_frequency: f.payout_frequency,
        override_pct: party.referred_by && f.override_pct !== "" ? Number(f.override_pct) : null,
        note: f.note || null,
      };
      const r = await api.post(`/hub/referrers/${party.id}/approve`, body);
      toast.success(r.data.approval_emailed ? "Approved — they've been told and the agreement is on its way" : "Approved");
      onDone?.(r.data);
      onOpenChange(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't approve"); }
    finally { setBusy(false); }
  };

  const decline = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/hub/referrers/${party.id}/decline`, { reason: reason || null });
      toast.success("Declined — a tactful note has gone to them");
      onDone?.(r.data);
      onOpenChange(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't decline"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={!!party} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl" data-testid="hub-approve-dialog">
        <DialogHeader>
          <DialogTitle className="text-[19px] tracking-[-0.01em]">Approve {party?.company || party?.name}?</DialogTitle>
          <DialogDescription className="text-[13px]">
            They signed up from the partner page{sg.requested_at ? ` on ${ukDay(sg.requested_at)}` : ""} and can already sign in.
            {sg.kind === "call_centre"
              ? " Their agent seats already work, so the floor can be set up — approving sets their terms, unlocks cases and issues the agreement + NDA to e-sign."
              : " Approving sets their terms, unlocks cases and links, and issues the agreement + NDA to e-sign."}
          </DialogDescription>
        </DialogHeader>

        <div className="rounded-[10px] border border-slate-200 bg-slate-50 px-3.5 py-3 text-[12.5px] leading-relaxed text-slate-600" data-testid="hub-approve-facts">
          <div className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1">
            <span className="text-slate-400">Kind</span><span className="text-slate-900">{KIND[sg.kind] || "Partner"}</span>
            <span className="text-slate-400">Contact</span><span className="text-slate-900">{party?.name} · {party?.email}{party?.phone ? ` · ${party.phone}` : ""}</span>
            {sg.website && <><span className="text-slate-400">Website</span><a href={sg.website.startsWith("http") ? sg.website : `https://${sg.website}`} target="_blank" rel="noreferrer" className="text-[#0055FF] underline underline-offset-2 truncate">{sg.website}</a></>}
            {sg.scale && <><span className="text-slate-400">{sg.kind === "media" ? "Reach" : "Agents"}</span><span className="text-slate-900">{sg.scale}</span></>}
            {Array.isArray(sg.channels) && sg.channels.length > 0 && <><span className="text-slate-400">Channels</span><span className="text-slate-900">{sg.channels.join(" · ")}</span></>}
            {party?.referred_by_name && <><span className="text-slate-400">Recruited by</span><span className="text-slate-900">{party.referred_by_name} · {party.override_pct}% override</span></>}
          </div>
        </div>

        {!declining ? (
          <div className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-[12px]">Commission model</Label>
                <Select value={f.commission_type} onValueChange={set("commission_type")}>
                  <SelectTrigger className="mt-1.5 h-10" data-testid="hub-approve-model"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {ADMIN_COMMISSION_TYPES.map((c) => (
                      <SelectItem key={c.key} value={c.key} data-testid={`hub-approve-model-${c.key}`}>{c.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {model?.hint && <p className="mt-1.5 text-[11px] leading-snug text-slate-500">{model.hint}</p>}
              </div>
              {isPct ? (
                <div>
                  <Label className="text-[12px]">{f.commission_type === "commission_share" ? "% of our commission" : "% of the funded amount"}</Label>
                  <Input type="number" min="0" max="100" step="0.5" value={f.commission_pct} onChange={set("commission_pct")} className="mt-1.5 h-10" data-testid="hub-approve-pct" />
                  <p className="mt-1.5 text-[11px] leading-snug text-slate-500">
                    {f.commission_type === "commission_share"
                      ? "Our usual deal: 50% of the fee we earn from the desk."
                      : "Careful — this is a share of the whole facility, not of our fee. 1–3% is the normal range."}
                  </p>
                </div>
              ) : (
                <div>
                  <Label className="text-[12px]">£ per {f.commission_type === "flat_lead" ? "qualified lead" : "funded deal"}</Label>
                  <Input type="number" min="0" step="10" value={f.commission_flat_gbp} onChange={set("commission_flat_gbp")} className="mt-1.5 h-10" data-testid="hub-approve-flat" />
                </div>
              )}
              <div>
                <Label className="text-[12px]">Payout schedule</Label>
                <Select value={f.payout_frequency} onValueChange={set("payout_frequency")}>
                  <SelectTrigger className="mt-1.5 h-10" data-testid="hub-approve-payout"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="monthly">Monthly (1st)</SelectItem>
                    <SelectItem value="weekly">Weekly (Fridays)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {party?.referred_by && (
                <div>
                  <Label className="text-[12px]">Recruiter override % (leave blank to keep {party.override_pct}%)</Label>
                  <Input type="number" min="0" max="100" step="0.5" value={f.override_pct} onChange={set("override_pct")} placeholder={String(party.override_pct)} className="mt-1.5 h-10" data-testid="hub-approve-override" />
                </div>
              )}
            </div>
            <div>
              <Label className="text-[12px]">Private note (optional)</Label>
              <Textarea value={f.note} onChange={set("note")} placeholder="Anything worth remembering about this partner" className="mt-1.5 min-h-[70px]" data-testid="hub-approve-note" />
            </div>
            <div className="flex items-center justify-between gap-3 pt-1">
              <button type="button" onClick={() => setDeclining(true)} className="text-[12.5px] font-semibold text-slate-500 hover:text-red-600 transition-colors" data-testid="hub-approve-decline-link">
                Decline instead
              </button>
              <Button onClick={approve} disabled={busy} className="rounded-full bg-[#0055FF] hover:bg-[#0044CC] h-10 px-5 font-semibold" data-testid="hub-approve-submit">
                {busy ? "Approving…" : "Approve and issue the agreement"}
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4" data-testid="hub-decline-pane">
            <div>
              <Label className="text-[12px]">Reason (private — the partner gets a tactful note, not this)</Label>
              <Textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. unverified media company, no trading history" className="mt-1.5 min-h-[80px]" data-testid="hub-decline-reason" />
            </div>
            <div className="flex items-center justify-between gap-3">
              <button type="button" onClick={() => setDeclining(false)} className="text-[12.5px] font-semibold text-slate-500 hover:text-slate-900" data-testid="hub-decline-back">Back</button>
              <Button onClick={decline} disabled={busy} variant="destructive" className="rounded-full h-10 px-5 font-semibold" data-testid="hub-decline-submit">
                {busy ? "Declining…" : "Decline this partner"}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
